import { ref, computed } from 'vue'
import { paymentService, type RenewalResponse } from '@/services/paymentService'
import { useToast } from '@/composables/useToast'

interface RenewalPlan {
  id: number
  display_name: string
  monthly_price: number
  annual_price: number
}

interface RenewalPaymentOptions {
  plan_id: number
  billing_cycle: 'monthly' | 'annual'
}

interface PaymentProofData {
  reference_number: string
  payment_date: string
  proof_image_url?: string
  notes?: string
}

export function useRenewalPayment(availablePlans: RenewalPlan[] = []) {
  const { showSuccess, showError } = useToast()
  
  // State
  const currentStep = ref(1)
  const loading = ref(false)
  const selectedPlanId = ref<number | null>(null)
  const billingCycle = ref<'monthly' | 'annual'>('monthly')
  const paymentData = ref<RenewalResponse | null>(null)
  const paymentDate = ref('')
  const proofImageUrl = ref('')
  const notes = ref('')

  // Computed
  const selectedPlan = computed(() => {
    return availablePlans.find(p => p.id === selectedPlanId.value)
  })

  const calculatedPrice = computed(() => {
    if (!selectedPlan.value) return 0
    return billingCycle.value === 'monthly' 
      ? selectedPlan.value.monthly_price 
      : selectedPlan.value.annual_price
  })

  // Methods
  const resetForm = () => {
    currentStep.value = 1
    selectedPlanId.value = null
    billingCycle.value = 'monthly'
    paymentData.value = null
    paymentDate.value = ''
    proofImageUrl.value = ''
    notes.value = ''
  }

  const nextStep = () => {
    currentStep.value++
  }

  const previousStep = () => {
    currentStep.value--
  }

  const requestRenewal = async (options: RenewalPaymentOptions) => {
    loading.value = true
    try {
      const response = await paymentService.requestRenewal(options)
      paymentData.value = response
      showSuccess('Solicitud de renovación creada')
      nextStep()
      return response
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Error al solicitar renovación'
      showError(message)
      throw error
    } finally {
      loading.value = false
    }
  }

  const copyReference = () => {
    if (paymentData.value?.reference_number) {
      navigator.clipboard.writeText(paymentData.value.reference_number)
      showSuccess('Número de referencia copiado')
    }
  }

  const submitProof = async (proofData: PaymentProofData) => {
    if (!paymentData.value) return

    loading.value = true
    try {
      await paymentService.submitPayment(paymentData.value.payment_id, proofData)
      showSuccess('Comprobante enviado para revisión')
      nextStep()
      return true
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Error al enviar comprobante'
      showError(message)
      throw error
    } finally {
      loading.value = false
    }
  }

  const preselectCurrentPlan = (currentPlanId?: number | null, currentBillingCycle?: 'monthly' | 'annual') => {
    if (currentPlanId && !selectedPlanId.value) {
      selectedPlanId.value = currentPlanId
      if (currentBillingCycle) {
        billingCycle.value = currentBillingCycle
      }
    }
  }

  // Watch for modal opening to pre-select current plan
  const watchModalOpen = (isOpen: boolean, currentPlanId?: number | null, currentBillingCycle?: 'monthly' | 'annual') => {
    if (isOpen) {
      preselectCurrentPlan(currentPlanId, currentBillingCycle)
    }
  }

  return {
    // State
    currentStep,
    loading,
    selectedPlanId,
    billingCycle,
    paymentData,
    paymentDate,
    proofImageUrl,
    notes,
    selectedPlan,
    calculatedPrice,
    
    // Methods
    resetForm,
    nextStep,
    previousStep,
    requestRenewal,
    copyReference,
    submitProof,
    watchModalOpen,
    preselectCurrentPlan
  }
}

